import { useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Bitcoin } from "lucide-react";
import { useBTCDominanceHistory, getDominanceLabel, getDominanceColor } from "@/hooks/useBTCDominance";
import { useBTCHistory } from "@/hooks/useBTCHistory";
import { formatUSD } from "@/lib/format";

interface BTCDominanceChartProps {
  days?: number;
  height?: number;
}

export const BTCDominanceChart = ({ days = 90, height = 180 }: BTCDominanceChartProps) => {
  const { data: dominanceData, isLoading: domLoading } = useBTCDominanceHistory(days);
  const { data: btcHistory, isLoading: btcLoading } = useBTCHistory('1Y');

  const isLoading = domLoading || btcLoading;

  // Merge dominance data with BTC price
  const chartData = useMemo(() => {
    if (!dominanceData?.length) return [];

    const btcMap = new Map<string, number>();
    if (btcHistory) {
      btcHistory.forEach((item) => {
        btcMap.set(item.date, item.price);
      });
    }

    return dominanceData.map((item) => {
      // Find closest BTC price
      let btcPrice = btcMap.get(item.date);
      
      // If no exact match, find closest date
      if (!btcPrice && btcHistory?.length) {
        const itemDate = new Date(item.date).getTime();
        let closest = btcHistory[0];
        let minDiff = Math.abs(new Date(closest.date).getTime() - itemDate);
        
        for (const h of btcHistory) {
          const diff = Math.abs(new Date(h.date).getTime() - itemDate);
          if (diff < minDiff) {
            minDiff = diff;
            closest = h;
          }
        }
        btcPrice = closest?.price;
      }

      return {
        date: item.date,
        dominance: Number(item.btc_dominance),
        btcPrice: btcPrice || null,
        timestamp: new Date(item.date).getTime(),
      };
    });
  }, [dominanceData, btcHistory]);

  // Get current and previous values for trend
  const currentValue = chartData[chartData.length - 1]?.dominance;
  const previousValue = chartData[chartData.length - 2]?.dominance;
  const trend = currentValue && previousValue 
    ? (currentValue > previousValue ? 'up' : currentValue < previousValue ? 'down' : 'neutral')
    : 'neutral';

  if (isLoading) {
    return <Skeleton className="w-full" style={{ height }} />;
  }

  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center text-muted-foreground" style={{ height }}>
        No dominance data available
      </div>
    );
  }

  const latest = chartData[chartData.length - 1];
  const minDom = Math.min(...chartData.map(d => d.dominance)) - 2;
  const maxDom = Math.max(...chartData.map(d => d.dominance)) + 2;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getDominanceColor(latest.dominance)}20` }}>
            <Bitcoin className="h-5 w-5" style={{ color: getDominanceColor(latest.dominance) }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{latest.dominance.toFixed(1)}%</span>
              {trend === 'up' && <TrendingUp className="h-4 w-4 text-profit" />}
              {trend === 'down' && <TrendingDown className="h-4 w-4 text-destructive" />}
            </div>
            <Badge 
              variant="outline" 
              className="text-xs"
              style={{ 
                borderColor: getDominanceColor(latest.dominance),
                color: getDominanceColor(latest.dominance)
              }}
            >
              {getDominanceLabel(latest.dominance)}
            </Badge>
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>BTC: {latest.btcPrice ? formatUSD(latest.btcPrice) : '—'}</div>
          <div>{days}d history</div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[hsl(38,92%,50%)]" />
          <span className="text-muted-foreground">BTC Dominance</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-muted-foreground" style={{ borderBottom: '2px dashed' }} />
          <span className="text-muted-foreground">BTC Price</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[hsl(38,92%,50%)]/20" />
          <span className="text-muted-foreground">&gt;55% BTC</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[hsl(280,70%,50%)]/20" />
          <span className="text-muted-foreground">&lt;45% Alt</span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          {/* Background zones */}
          <ReferenceArea y1={55} y2={maxDom} yAxisId="dominance" fill="hsl(38, 92%, 50%)" fillOpacity={0.1} />
          <ReferenceArea y1={minDom} y2={45} yAxisId="dominance" fill="hsl(280, 70%, 50%)" fillOpacity={0.1} />
          <ReferenceArea y1={45} y2={55} yAxisId="dominance" fill="hsl(var(--muted))" fillOpacity={0.1} />

          {/* Reference lines */}
          <ReferenceLine y={55} yAxisId="dominance" stroke="hsl(38, 92%, 50%)" strokeDasharray="3 3" strokeOpacity={0.5} />
          <ReferenceLine y={45} yAxisId="dominance" stroke="hsl(280, 70%, 50%)" strokeDasharray="3 3" strokeOpacity={0.5} />

          <defs>
            <linearGradient id="dominanceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(38, 92%, 50%)" stopOpacity={0.4} />
              <stop offset="50%" stopColor="hsl(38, 92%, 50%)" stopOpacity={0.2} />
              <stop offset="100%" stopColor="hsl(38, 92%, 50%)" stopOpacity={0.05} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />

          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(ts) => format(new Date(ts), 'MMM d')}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
            minTickGap={40}
          />

          <YAxis
            yAxisId="dominance"
            orientation="left"
            domain={[minDom, maxDom]}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={45}
          />

          <YAxis
            yAxisId="price"
            orientation="right"
            domain={['auto', 'auto']}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            width={50}
          />

          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const data = payload[0].payload;
              return (
                <div className="bg-popover/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-xl">
                  <div className="text-xs text-muted-foreground mb-2">
                    {format(new Date(data.date), 'MMM d, yyyy')}
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs text-muted-foreground">Dominance</span>
                      <span className="text-sm font-medium" style={{ color: getDominanceColor(data.dominance) }}>
                        {data.dominance.toFixed(2)}%
                      </span>
                    </div>
                    {data.btcPrice && (
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-xs text-muted-foreground">BTC Price</span>
                        <span className="text-sm font-medium">
                          {formatUSD(data.btcPrice)}
                        </span>
                      </div>
                    )}
                    <Badge variant="outline" className="text-xs w-full justify-center mt-1">
                      {getDominanceLabel(data.dominance)}
                    </Badge>
                  </div>
                </div>
              );
            }}
          />

          {/* Dominance area with gradient */}
          <Area
            yAxisId="dominance"
            type="monotone"
            dataKey="dominance"
            stroke="hsl(38, 92%, 50%)"
            strokeWidth={2}
            fill="url(#dominanceGradient)"
          />

          {/* BTC Price line */}
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="btcPrice"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            dot={false}
            opacity={0.5}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Zone labels */}
      <div className="flex justify-between text-[10px] text-muted-foreground px-2">
        <span>← Alt Season</span>
        <span>Neutral Zone</span>
        <span>BTC Season →</span>
      </div>
    </div>
  );
};
